import type { FormApi } from '../../form-hooks/useFormApi';
import type { SpaceFormSubmitFn } from '../../form/SpaceForm/SpaceForm';

import { useQueryClient } from '@tanstack/react-query';
import { use, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';

import { postSpace, updateSpacePlan } from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import {
  generateRedirectUrl,
  getTestProps,
  sendEventAndClear,
} from '../../lib/helpers';

import Heading from '../../components/Heading/Heading';
import LinkButton from '../../components/LinkButton/LinkButton';
import Loader from '../../components/Loader/Loader';
import Panel from '../../components/Panel/Panel';
import StatusPill from '../../components/StatusPill/StatusPill';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarDeleteButton from '../../components/Topbar/buttons/TopbarDeleteButton';
import TopbarSynchronizationButton from '../../components/Topbar/buttons/TopbarExportButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';

import {
  useAllPublicPlans,
  useConstraints,
  useSpace,
  useUser,
  useUsers,
} from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import { useSpaceConstraint } from '../../hooks/api/useSpaceConstraint';
import useSpaceSynchronization from '../../hooks/synchronization/useSpaceSynchronization';
import useToken from '../../hooks/useToken';

import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import { ExportIcon, ImportIcon, SynchronizeIcon } from '../../images/shapes';

import SpaceForm from '../../form/SpaceForm/SpaceForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import SpaceLimits from '../SpacesManage/SpaceLimits/SpaceLimits';

const USERS_HOOK_PARAMS = {
  page: 1,
  limit: 1000,
};

const testId = 'space-modify';

interface SpaceModifyProps {
  /**
   * Allow to use additional options. "upgrade" | "edit" | "add"
   */
  mode?: string;
  /**
   *  Delay timeout for redirect to page after success change subscription
   */
  delaySub?: number;
}

const SpaceModify = ({ mode = 'add', delaySub = 5000 }: SpaceModifyProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { isAdmin, userStorage, baseUserEventData } = use(UserContext);

  const modal = useModals();
  const jwt = useToken();
  const { id: spaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [isSpaceSaving, setIsSpaceSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingPurchase, setIsLoadingPurchase] = useState(false);

  const [formState, setFormState] = useState<FormApi>();

  const usersOptions = useMemo(
    () => ({
      pause: mode !== 'edit',
    }),
    [mode],
  );

  const { data: users, isLoading: usersAreLoading } = useUsers(
    USERS_HOOK_PARAMS,
    usersOptions,
  );

  const constraintsOptions = useMemo(
    () => ({
      pause: !isAdmin,
    }),
    [isAdmin],
  );

  const { entity: spacesFreeCount } = useConstraints(
    'spaces-free',
    null,
    constraintsOptions,
  );

  const { reload: reloadConstraint } = useSpaceConstraint(
    'team-members-count',
    spaceId,
  );

  const spaceOptions = useMemo(
    () => ({
      pause: !isAdmin || !spaceId,
    }),
    [isAdmin, spaceId],
  );

  const {
    entity: spaceData,
    updateEntity: updateSpaceData,
    deleteEntity: removeSpaceData,
    errors: spaceDataErrors,
    isLoading: isLoadingSpaceData,
  } = useSpace(spaceId, null, spaceOptions);

  const { userStorage: userDetails } = use(UserContext);

  const { entity: user, isLoading: isLoadingUser } = useUser(
    userDetails?.data?.id,
  );

  useApiErrorsToast(spaceDataErrors);

  const plansParams = useMemo(
    () => ({
      page: 1,
      limit: 10,
    }),
    [],
  );

  const { data: allPlans, isLoading: isLoadingAllPlans } =
    useAllPublicPlans(plansParams);

  const { handleExport, handleImport, handleSynchronize, isSpaceAvailable } =
    useSpaceSynchronization(spaceId);

  const handleSpaceEdit = useCallback<SpaceFormSubmitFn>(
    async (data) => {
      setIsSpaceSaving(true);
      try {
        const { body, status, ok } = await updateSpaceData(data);

        // Check response status
        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        // Toast with success on update
        toast.success(t('Spaces.Form.Updated'));

        setIsSpaceSaving(false);

        queryClient.invalidateQueries({ queryKey: ['users'] });
        reloadConstraint();

        // Return selected data
        return [[data, {}], false];
      } catch (error) {
        // Error instance
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          setIsSpaceSaving(false);
          return [[data, {}], true];
        } else {
          // Toast with error on update space
          toast.error(error.message || t('Spaces.Form.CouldntUpdate'));

          setIsSpaceSaving(false);

          // Return selected data and error
          return [[data, error.errors], true];
        }
      }
    },
    [queryClient, reloadConstraint, t, updateSpaceData],
  );

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const handleDeleteSpace = useCallback(async () => {
    modal.deleting('delete-modal');
    setIsDeleting(true);

    try {
      const { body, status, ok } = await removeSpaceData({ id: spaceId });

      if (!ok) {
        throw parseResponseErrors(body, status);
      }
      toast.success(t('Spaces.Form.Removed'));
      setIsDeleting(false);

      sendEventAndClear(
        {
          event: 'space_delete',
          space_id: spaceId,
          plan_id: spaceData?.planLimits?.id,
          plan_name: spaceData?.planLimits?.name,
        },
        baseUserEventData,
      );

      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.removeQueries({
        queryKey: ['spaces'],
      });

      navigate('/organization');
    } catch (error) {
      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
      } else if (
        error?.errors?.space?.[0]?.indexOf('has assigned users') > -1
      ) {
        toast.error(t('Spaces.Form.CouldntRemovedHasUsers'));
      } else if (
        error?.errors?.data?.[0]?.includes(
          'Deletion will be possible after the subscription ends',
        )
      ) {
        const endDateGroup = error.errors.data[0].match(
          /until(?<date>[^.\]]*)/,
        )?.groups;
        toast.error(
          t('Spaces.Form.CouldntRemovedSubscription', {
            name: spaceData?.name,
            date: moment(endDateGroup?.date).format('L LT'),
          }),
          { duration: 5000 },
        );
      } else {
        toast.error(t('Spaces.Form.CouldntRemoved'));
      }

      setIsDeleting(false);
    }

    setIsDeleting(false);
  }, [
    modal,
    removeSpaceData,
    spaceId,
    t,
    spaceData?.planLimits?.id,
    spaceData?.planLimits?.name,
    spaceData?.name,
    baseUserEventData,
    queryClient,
    navigate,
  ]);

  const deleteSpace = useCallback(async () => {
    setIsDeleting(true);
    await modal.delete(t('Spaces.ConfirmDelete'), 'delete-modal', () =>
      handleDeleteSpace(),
    );
    setIsDeleting(false);
  }, [handleDeleteSpace, modal, t]);

  const handleCreateSpace = useCallback(
    async (data: Parameters<SpaceFormSubmitFn>[0], redirectPath: string) => {
      try {
        const { body, status, ok } = await postSpace(jwt, undefined, {
          name: data.name,
          planId: data.planId,
          period: data.paymentInterval,
          uri: generateRedirectUrl(redirectPath),
        });

        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        setIsSpaceSaving(false);

        if (body.payment_url) {
          window.location.assign(body.payment_url);
        } else {
          toast.success(t('Spaces.Form.Added'));

          sendEventAndClear(
            {
              event: 'space_add',
              space_id: body.id,
              plan_id: data.planId,
              plan_name: data.planName,
            },
            baseUserEventData,
          );

          navigate('/organization');
          queryClient.removeQueries({ queryKey: ['spaces'] });
          queryClient.invalidateQueries({ queryKey: ['users'] });
        }
        return [[body, {}], false];
      } catch (error) {
        setIsSpaceSaving(false);

        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [[data, {}], true];
        }

        if (error.message) {
          toast.error(error.message);
        } else {
          toast.error(t('Spaces.Form.CouldntAdd'));
        }

        return [[data, error.errors], true];
      }
    },
    [jwt, t, baseUserEventData, navigate, queryClient],
  );

  const navigateOnUpgrade = useCallback(
    (body: { status: string; url: string }, redirectPath: string) => {
      if (body.status === 'payment_required') {
        window.location.assign(body.url);
      } else {
        if (body.status === 'plan_not_changed') {
          navigate('/organization?cancel=1&type=plan_not_changed');
        } else if (body.status === 'subscription_updated') {
          // Case need time to proces billing and update space
          setIsLoadingPurchase(true);
          setTimeout(() => {
            navigate(redirectPath + '&success=1&type=plan_sub_update');
          }, delaySub);
        } else if (body.status === 'cancel_at_period_end') {
          navigate('/organization?cancel=1&type=plan_period_end');
        }
      }
    },
    [delaySub, navigate],
  );

  const handleUpgradeSpace = useCallback(
    async (data: Parameters<SpaceFormSubmitFn>[0], redirectPath: string) => {
      const currentInterval = spaceData?.paymentInterval || 'month';
      const isChangedInterval = currentInterval !== data.paymentInterval;
      const isChangedPlanName =
        data.planVisibleName !== spaceData?.planLimits?.visible_name;

      if (
        !data.isFreePlan &&
        spaceData?.planLimits?.price !== 0 &&
        (isChangedPlanName || isChangedInterval)
      ) {
        const confirmation = await modal.confirmation(
          <Trans
            i18nKey="Spaces.Form.UpdateInformation"
            values={{
              oldPlanName: spaceData?.planLimits?.visible_name,
              oldInterval: t(`Spaces.Form.Period.${currentInterval}`),
              newPlanName: data.planVisibleName,
              newInterval: t(`Spaces.Form.Period.${data.paymentInterval}`),
            }}
          />,
          t('Global.Information'),
          t('Global.Continue'),
          '',
          'simple',
        );

        if (!confirmation) {
          setIsSpaceSaving(false);
          return;
        }
      }

      try {
        const { body, status, ok } = await updateSpacePlan(jwt, undefined, {
          id: spaceId,
          planId: data.planId,
          period: data.paymentInterval,
          uri: generateRedirectUrl(redirectPath),
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        setIsSpaceSaving(false);
        navigateOnUpgrade(body, redirectPath);

        return [[data, {}], false];
      } catch (error) {
        setIsSpaceSaving(false);

        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [[data, {}], true];
        }

        toast.error(t('Spaces.Form.CouldntAdd'));

        return [[data, error.errors], true];
      }
    },
    [
      spaceData?.paymentInterval,
      spaceData?.planLimits?.visible_name,
      spaceData?.planLimits?.price,
      modal,
      t,
      jwt,
      spaceId,
      navigateOnUpgrade,
    ],
  );

  const handleSpaceCreateUpgrade = useCallback<SpaceFormSubmitFn>(
    async (data) => {
      setIsSpaceSaving(true);

      let redirectPath = `/organization?action=${mode}&planId=${data.planId}&planName=${data.planName}`;

      if (spaceId) redirectPath += `&spaceId=${spaceId}`;
      if (location?.state?.upgrade_source)
        redirectPath += `&upgradeSource=${location.state.upgrade_source}`;

      if (mode === 'add') await handleCreateSpace(data, redirectPath);
      else await handleUpgradeSpace(data, redirectPath);

      return [[data, null], false];
    },
    [mode, spaceId, location, handleCreateSpace, handleUpgradeSpace],
  );

  const sectionHead = useMemo(() => {
    const res = { title: '', subtitle: '' };

    if (mode === 'add') {
      res.title = t('Spaces.CreateASpaceForOrganization', {
        name: userStorage?.data?.organization_name || '',
      });

      res.subtitle = t('Spaces.SpaceDetails');
    } else if (mode === 'upgrade') {
      res.title = `${t('Spaces.UpgradeSpace')} ${spaceData?.name || ''}`;
    } else if (mode === 'edit') {
      res.title = t('Spaces.EditSpace', { name: spaceData?.name });
    }

    return res;
  }, [mode, t, spaceData?.name, userStorage?.data?.organization_name]);

  const isFormVisible = useMemo(() => {
    if (mode === 'add') {
      return !isLoadingAllPlans || !isLoadingUser;
    } else {
      return !isLoadingSpaceData && !spaceDataErrors;
    }
  }, [
    mode,
    isLoadingAllPlans,
    isLoadingSpaceData,
    spaceDataErrors,
    isLoadingUser,
  ]);

  const userOptions = useMemo(
    () =>
      users?.map((user) => {
        const rolesData = user.roles?.map((role) =>
          role
            .replace('ROLE_HEADLESS_ADMIN', 'Organization Admin')
            .replace('ROLE_HEADLESS_USER', 'Organization User')
            .replace('ROLE_', ''),
        );
        return {
          value: user.id,
          searchString: user.email + ' ' + rolesData?.join(' '),
          label: user.email,
          optionLabel: (
            <span
              className={
                'w-full flex flex-col sm:flex-row flex-wrap gap-1 sm:items-center sm:justify-between mr-1.5'
              }
            >
              {user.email}
              <span className="flex gap-1 ml-auto">
                {rolesData?.map((role) => (
                  <StatusPill key={role} status={role} />
                ))}
              </span>
            </span>
          ),
        };
      }),
    [users],
  );

  const pageTitle = useMemo(() => {
    if (mode === 'upgrade') {
      return t('Spaces.UpgradeSpace');
    } else if (mode === 'edit') {
      return t('Spaces.EditSpace', { name: '' });
    }
    return t('Spaces.CreateANewSpace');
  }, [mode, t]);

  return (
    <PageLayout
      page="spaces-and-usage"
      title={pageTitle}
      buttonsDisabled={isSpaceSaving || isDeleting}
      buttons={
        <>
          <TopbarCancelButton
            isDirty={formState?.dirty}
            link={'/organization'}
          />
          {mode === 'edit' && (
            <TopbarSaveButton
              form="space-form"
              isLoading={isSpaceSaving}
              disabled={!!spaceDataErrors}
            />
          )}
          <TopbarActionMenu>
            {mode === 'edit' && (
              <>
                <TopbarSynchronizationButton
                  onClick={() =>
                    handleExport('exportSpace', spaceData?.name || '')
                  }
                  iconImage={<ExportIcon className="w-5 mb-px" />}
                  disabled={!isSpaceAvailable}
                  tooltip={
                    !isSpaceAvailable
                      ? t('SpaceSynchronization.Import.SpaceAssignedBlockade')
                      : ''
                  }
                  testIdSuffix="export"
                  label={t('SpaceSynchronization.Export.ExportButton')}
                />
                <TopbarSynchronizationButton
                  onClick={() =>
                    handleImport('importSpace', spaceData?.name || '')
                  }
                  iconImage={<ImportIcon className="w-5 mb-px" />}
                  disabled={!isSpaceAvailable}
                  tooltip={
                    !isSpaceAvailable
                      ? t('SpaceSynchronization.Export.SpaceAssignedBlockade')
                      : ''
                  }
                  testIdSuffix="import"
                  label={t('SpaceSynchronization.Import.ImportButton')}
                />
                <TopbarSynchronizationButton
                  onClick={handleSynchronize}
                  iconImage={<SynchronizeIcon className="w-5 mb-px" />}
                  disabled={!isSpaceAvailable}
                  tooltip={
                    !isSpaceAvailable
                      ? t(
                          'SpaceSynchronization.Synchronize.SpaceAssignedBlockade',
                        )
                      : ''
                  }
                  testIdSuffix="synchronize"
                  label={t('SpaceSynchronization.Synchronize.SynchronizeSpace')}
                />
                <TopbarDeleteButton
                  onClick={deleteSpace}
                  disabled={!!spaceDataErrors}
                />
              </>
            )}
          </TopbarActionMenu>
        </>
      }
      testId={testId}
    >
      <div
        className={mode === 'edit' ? predefinedLayoutClasses.withSidebar : ''}
      >
        <div
          className={twMerge(
            'relative md:min-h-[80vh]',
            predefinedLayoutClasses.leftColumnWhite,
            mode === 'edit' &&
              `flex flex-col lg:flex-row gap-10 ${
                isFormVisible ? 'px-4 md:px-10 py-6' : ''
              }`,
          )}
        >
          {(isLoadingUser ||
            isLoadingAllPlans ||
            (mode !== 'add' && isLoadingSpaceData) ||
            isLoadingPurchase) && (
            <div
              className={twMerge(
                'absolute left-0 top-0 z-20 w-full h-full flex',
                'justify-center items-center bg-white dark:bg-slate-950 rounded-lg opacity-80',
                isLoadingPurchase && 'opacity-100',
              )}
              {...getTestProps(testId, 'loader')}
            >
              <Loader type="spinner-grid" />
            </div>
          )}
          {isFormVisible && (
            <div
              className={twMerge(
                'flex flex-col w-full',
                isSpaceSaving && 'pointer-events-none',
                mode !== 'edit' && 'justify-center p-4 md:py-6 md:px-10',
              )}
              {...getTestProps(testId, 'form-container')}
            >
              <Heading level={3} additionalClasses="py-2 dark:text-slate-200">
                {sectionHead.title}
              </Heading>

              {sectionHead.subtitle && (
                <Heading
                  level={4}
                  additionalClasses="pb-2 pt-4 mt-2 dark:text-slate-200 border-t border-slate-200 dark:border-slate-800"
                >
                  {sectionHead.subtitle}
                </Heading>
              )}
              <SpaceForm
                onSubmit={
                  mode === 'edit' ? handleSpaceEdit : handleSpaceCreateUpgrade
                }
                space={mode !== 'add' ? spaceData : undefined}
                mode={mode}
                userOptions={userOptions}
                usersAreLoading={usersAreLoading}
                spacesFreeCount={spacesFreeCount?.data}
                allPlans={allPlans}
                disabled={isSpaceSaving}
                setFormState={setFormState}
                user={user}
                testId={testId}
              />
            </div>
          )}
        </div>

        {isFormVisible && mode === 'edit' && (
          <div className={predefinedLayoutClasses.rightColumn}>
            <Panel
              title={
                <>
                  <Heading level={4} additionalClasses="dark:text-slate-200">
                    {t('Spaces.SpaceLimits')}
                  </Heading>

                  <LinkButton
                    link={`/space/upgrade/${spaceId}`}
                    disabled={
                      !spaceData?.planLimits?.price ||
                      spaceData.planLimits.price < 0
                    }
                    {...getTestProps(testId, 'upgrade-space', 'testId')}
                  >
                    {t('Spaces.UpgradeSpace')}
                  </LinkButton>
                </>
              }
              additionalTitleWrapClasses="w-full"
              additionalTitleClasses="flex justify-between items-center w-full"
              isCollapsable={false}
              open
            >
              <SpaceLimits
                key={'' + spaceId + spaceData?.users?.length}
                id={spaceId}
                planLimits={spaceData?.planLimits}
                isBuyExtraVisible={false}
              />
            </Panel>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default SpaceModify;
